<template>
  <div
    id="app-container"
    class="relative min-h-screen"
    :class="[isWatchdogUIEnabled ? 'watchdog' : '']"
  >
    <IdentityVerification v-if="showIDWFlow" />
    <router-view v-else />
  </div>
</template>

<script>
import Watchdog from '@/mixins/Watchdog';
import dayjs from 'dayjs';
import churnzeroService from '@/services/churnzero';
import IdentityVerification from '@/components/IdentityVerification.vue';
// import { getBrowserInfo } from "@/common/browserInfo";

export default {
  name: 'App',
  components: {
    IdentityVerification,
  },
  mixins: [Watchdog],
  data() {
    return {
      browserInfo: {},
      hasStartedLoadAlertPolling: false,
    };
  },
  beforeCreate() {
    // Initialize the store from localStore for things
    // like currentLane.  Very limited usage please.
    this.$store.dispatch('loading/initializeStore');
    this.$store.dispatch('laneSearches/initializeStore');
    this.$store.dispatch('carriers/initializeStore');
    this.$store.dispatch('brokerPortal/initializeStore');
    this.$store.dispatch('loads/initializeStore');
    // this.browserInfo = getBrowserInfo();
  },
  mounted() {
    window.addEventListener('vite:preloadError', (event) => {
      event.preventDefault();
    });
    this.$store.dispatch('autoTest/runAutoTests');
    this.initToastObserver();
  },
  computed: {
    isCompanyEnforced() {
      return this.$store.state.users.currentUser?.company?.broker_idv_required;
    },
    showIDWFlow() {
      const { currentUser } = this.$store.state.users;
      const inquiry = this.$store.getters['users/getLatestInquiry'];
      const status = currentUser?.identity_proofing_status;

      // Early exits: user already verified
      if (currentUser?.last_verified_identity_at) return false;
      if (['approved', 'under_review'].includes(inquiry?.status)) return false;

      // If company is not enforced, don't show flow
      if (!this.isCompanyEnforced) return false;

      // Company is enforced - check identity proofing status
      if (status === 'skip_identity_proofing') return false;
      if (status === 'require_identity_proofing') return true;

      // Neutral state: company is enforced, so show flow
      return true;
    },
    isInsuranceProducer() {
      return (
        this.$store.state.users.currentUser?.company?.company_type ===
          'insurance_producer' &&
        (this.$store.state.users.currentUser?.scopes?.includes(
          'view_tickets'
        ) ||
          this.$store.state.users.currentUser?.scopes?.includes(
            'manage_tickets'
          ))
      );
    },
    canViewLoadLockAlerts() {
      return (
        this.$store.state.users.currentUser?.paywall?.can_load_dashboard &&
        (this.$store.state.users.currentUser?.scopes?.includes(
          'view_tickets'
        ) ||
          this.$store.state.users.currentUser?.scopes?.includes(
            'manage_tickets'
          ) ||
          this.$store.state.users.currentUser?.scopes?.includes(
            'manage_tickets_and_notes'
          ))
      );
    },
  },
  beforeUnmount() {
    this.shutdownIntercom();
    this.toastObserver.disconnect();
  },
  unmounted() {
    window.removeEventListener('vite:preloadError');
  },
  methods: {
    startLoadAlertsPolling() {
      if (this.hasStartedLoadAlertPolling) {
        return;
      }

      this.hasStartedLoadAlertPolling = true;
      console.log('[Polling] Started');

      this.$store.dispatch('loads/getAlertCount', ['new']);

      setInterval(() => {
        this.$store.dispatch('loads/getAlertCount', ['new']);
      }, 240000);
    },
    initToastObserver() {
      this.toastObserver = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type !== 'childList' || !mutation.addedNodes.length)
            continue;

          for (let node of mutation.addedNodes) {
            if (
              !node.classList ||
              !node.classList.contains('v-toast__item--success')
            )
              continue;

            if (this.isWatchdogUIEnabled) {
              node.style.backgroundColor = 'rgb(14,39,83)';
            }

            const existingIcon = node.querySelector('.v-toast__icon');
            existingIcon?.remove();

            const newIcon = document.createElement('div');
            newIcon.classList.add('v-toast__icon');

            const yellowIcon = `background-image: url("data:image/svg+xml,%3Csvg width='27' height='27' viewBox='0 0 13 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M6.30466 12.2119C9.35139 12.2119 11.8212 9.74201 11.8212 6.69529C11.8212 5.8144 11.6148 4.98173 11.2476 4.24303L6.49714 8.99348L5.93988 9.55074L5.38262 8.99348L2.95534 6.56621L4.06986 5.45169L5.93988 7.32171L10.3341 2.92748C9.32739 1.8513 7.89458 1.17871 6.30466 1.17871C3.25794 1.17871 0.788086 3.64857 0.788086 6.69529C0.788086 9.74201 3.25794 12.2119 6.30466 12.2119Z' fill='%23f8c617'/%3E%3C/svg%3E%0A") !important;`;
            const whiteIcon = `background-image: url("data:image/svg+xml,%3Csvg width='27' height='27' viewBox='0 0 13 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M6.30466 12.2119C9.35139 12.2119 11.8212 9.74201 11.8212 6.69529C11.8212 5.8144 11.6148 4.98173 11.2476 4.24303L6.49714 8.99348L5.93988 9.55074L5.38262 8.99348L2.95534 6.56621L4.06986 5.45169L5.93988 7.32171L10.3341 2.92748C9.32739 1.8513 7.89458 1.17871 6.30466 1.17871C3.25794 1.17871 0.788086 3.64857 0.788086 6.69529C0.788086 9.74201 3.25794 12.2119 6.30466 12.2119Z' fill='%23FFFFFF'/%3E%3C/svg%3E%0A") !important;`;

            newIcon.setAttribute(
              'style',
              this.isWatchdogUIEnabled ? whiteIcon : yellowIcon
            );
            node.insertBefore(newIcon, node.firstChild);
          }
        }
      });

      this.toastObserver.observe(document, { childList: true, subtree: true });
    },
    async bootIntercom() {
      if (process.env.ENABLE_INTERCOM != 1) {
        return;
      }
      await window.Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: process.env.INTERCOM_APP_ID,
        hide_default_launcher: true,
        custom_launcher_selector: '#launch_highway_intercom',
      });
      this.$store.commit('users/setIntercomIsBooted', true);
    },
    async bootOrUpdateIntercomWithUser(currentUser) {
      if (process.env.ENABLE_INTERCOM != 1) {
        return;
      }
      if (this.$store.state.users.intercomIsBooted === false) {
        if (currentUser?.id > 0) {
          await window.Intercom('boot', {
            api_base: 'https://api-iam.intercom.io',
            app_id: process.env.INTERCOM_APP_ID,
            hide_default_launcher: true,
            custom_launcher_selector: '#launch_highway_intercom',
            user_id: currentUser?.id,
            user_hash: currentUser?.intercom_user_verification_hash,
            name: `${currentUser?.first_name} ${currentUser?.last_name}`,
            email: currentUser?.email, // the email for your user
            created_at: dayjs(currentUser?.created_at)?.unix(),
            phone: currentUser?.phone_e164,
            company: {
              id: currentUser?.company?.id,
              name: currentUser?.company?.legal_name,
              created_at: dayjs(currentUser?.company?.created_at)?.unix(),
              plan_id: currentUser?.company?.plan_id,
              company_status: currentUser?.company?.status,
            },
            // browser: this.browserInfo?.browserName,
            // browser_version: this.browserInfo?.majorBrowserVersion,
            // browser_full_version: this.browserInfo?.fullBrowserVersion,
            // os: this.browserInfo?.osName,
          });
          this.$store.commit('users/setIntercomIsUserLoggedIn', true);
        }
      } else {
        if (currentUser?.id > 0) {
          await window.Intercom('update', {
            user_id: currentUser?.id,
            user_hash: currentUser?.intercom_user_verification_hash,
            name: `${currentUser?.first_name} ${currentUser?.last_name}`,
            email: currentUser?.email, // the email for your user
            created_at: dayjs(currentUser?.created_at)?.unix(),
            phone: currentUser?.phone_e164,
            company: {
              id: currentUser?.company?.id,
              name: currentUser?.company?.legal_name,
              created_at: dayjs(currentUser?.company?.created_at)?.unix(),
              plan_id: currentUser?.company?.plan_id,
              company_status: currentUser?.company?.status,
            },
            // browser: this.browserInfo?.browserName,
            // browser_version: this.browserInfo?.majorBrowserVersion,
            // browser_full_version: this.browserInfo?.fullBrowserVersion,
            // os: this.browserInfo?.osName,
          });
          this.$store.commit('users/setIntercomIsUserLoggedIn', true);
        }
      }
      this.$store.commit('users/setIntercomIsBooted', true);
    },
    shutdownIntercom() {
      if (process.env.ENABLE_INTERCOM != 1) {
        return;
      }
      window.Intercom('shutdown');
      this.$store.commit('users/setIntercomIsBooted', false);
      this.$store.commit('users/setIntercomIsUserLoggedIn', false);
    },
  },
  watch: {
    '$store.state.users.currentUser': function (newVal) {
      if (newVal?.id > 0) {
        if (this.canViewLoadLockAlerts || this.isInsuranceProducer) {
          this.startLoadAlertsPolling();
        }
        if (this.$store.state.users.intercomIsUserLoggedIn === false) {
          this.bootOrUpdateIntercomWithUser(newVal);
        }
        if (this.$rollbar?.client?.options?.payload) {
          let co = newVal?.company?.legal_name?.replace(/ /g, '-');
          let username = null;
          if (co?.length > 0) {
            username = co + '===' + newVal?.company?.id + '__' + newVal?.id;
          } else {
            username = 'None__' + newVal?.id;
          }
          this.$rollbar.client.options.payload['person'] = {
            id: newVal?.id,
            username: username,
          };
        }
      } else {
        if (this.$store.state.users.intercomIsBooted === false) {
          this.bootIntercom();
        }
      }
      // Only initialize ChurnZero once, not on every user change
      if (!churnzeroService.isReady()) {
        churnzeroService.initialize();
      }
    },
  },
};
</script>
